import axios from 'axios';
import { RESEND_CONFIG } from '../config/resend.config';

interface EmailData {
  to_email: string;
  from_name: string;
  from_email: string;
  phone?: string;
  insurance_company?: string;
  annuity_product?: string;
  year_purchased: string;
  current_age: string;
  initial_investment?: string;
  primary_goal?: string;
  message: string;
}

// Service to handle email sending via Resend
export const resendService = {
  // Send email via Resend API
  async sendEmail(formData: any): Promise<void> {
    console.log('=== RESEND SERVICE: sendEmail START ===');
    console.log('Received form data:', JSON.stringify(formData, null, 2));
    
    const emailData = this.formatEmailContent(formData);
    console.log('Formatted email data:', JSON.stringify(emailData, null, 2));
    console.log('API Endpoint:', RESEND_CONFIG.API_ENDPOINT);
    
    // Check if we're in client-only mode
    if (RESEND_CONFIG.API_ENDPOINT === 'client-only') {
      console.log('Client-only mode detected, no backend available');
      console.log('Cannot send email without backend server');
      throw new Error('Email service unavailable. Please try again later.');
    }
    
    try {
      console.log('Making POST request to:', RESEND_CONFIG.API_ENDPOINT);
      const requestPayload = { data: emailData };
      console.log('Request payload:', JSON.stringify(requestPayload, null, 2));
      
      const response = await axios.post(RESEND_CONFIG.API_ENDPOINT, requestPayload);
      
      console.log('Response received:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers
      });
      
      if (response.data.success) {
        console.log('Email sent successfully via Resend to jake@averillagencies.com');
        console.log('=== RESEND SERVICE: sendEmail SUCCESS ===');
        return response.data;
      } else {
        console.error('Server returned success=false');
        console.error('Response data:', response.data);
        throw new Error('Failed to send email - server returned success=false');
      }
    } catch (error: any) {
      console.error('Error in resendService.sendEmail:', error);
      console.error('Error type:', error?.constructor?.name);
      console.error('Error details:', {
        message: error?.message,
        code: error?.code,
        response: error?.response,
        responseData: error?.response?.data,
        responseStatus: error?.response?.status,
        responseStatusText: error?.response?.statusText,
        isAxiosError: error?.isAxiosError,
        config: error?.config ? {
          url: error.config.url,
          method: error.config.method,
          headers: error.config.headers,
          data: error.config.data
        } : undefined
      });
      
      // Log API failures but don't open mailto
      if (error.response?.status === 404 || error.code === 'ECONNREFUSED') {
        console.log('Server not available');
      } else if (error.code === 'ERR_NETWORK') {
        console.log('Network error detected');
      } else if (error.response?.status === 400) {
        console.log('Bad request (possibly API key issue)');
      }
      
      console.log('=== RESEND SERVICE: sendEmail ERROR ===');
      throw error;
    }
  },
  
  // Check if the email server is running
  async checkHealth(): Promise<boolean> {
    try {
      const response = await axios.get(RESEND_CONFIG.HEALTH_ENDPOINT);
      return response.data.status === 'OK';
    } catch (error) {
      console.log('Email server not available');
      return false;
    }
  },
  
  
  // Format email data for Resend
  formatEmailContent(data: any): EmailData {
    // Check if this is already formatted with a custom message
    if (data.message) {
      return {
        to_email: RESEND_CONFIG.RECIPIENT_EMAIL,
        from_name: data.fullName,
        from_email: data.email,
        phone: data.phone || 'Not provided',
        insurance_company: data.insuranceCompany || 'Not provided',
        annuity_product: data.annuityProduct || 'Not provided',
        year_purchased: data.yearPurchased || new Date().getFullYear().toString(),
        current_age: data.currentAge || 'N/A',
        initial_investment: data.initialInvestment || 'Not provided',
        primary_goal: data.primaryGoal || 'Not provided',
        message: data.message
      };
    }
    
    // Default formatting for standard annuity analysis
    return {
      to_email: RESEND_CONFIG.RECIPIENT_EMAIL,
      from_name: data.fullName,
      from_email: data.email,
      phone: data.phone || 'Not provided',
      insurance_company: data.insuranceCompany || 'Not provided',
      annuity_product: data.annuityProduct || 'Not provided',
      year_purchased: data.yearPurchased,
      current_age: data.currentAge,
      initial_investment: data.initialInvestment || 'Not provided',
      primary_goal: data.primaryGoal || 'Not provided',
      message: `
New Annuity Analysis Request

Contact Information:
Name: ${data.fullName}
Email: ${data.email}
Phone: ${data.phone || 'Not provided'}

Annuity Details:
Insurance Company: ${data.insuranceCompany || 'Not provided'}
Annuity Product: ${data.annuityProduct || 'Not provided'}
Year Purchased: ${data.yearPurchased}
Current Age: ${data.currentAge}
Initial Investment: ${data.initialInvestment || 'Not provided'}
Primary Goal: ${data.primaryGoal || 'Not provided'}

Submitted on: ${new Date().toLocaleString()}
      `.trim()
    };
  }
};